import { motion } from "framer-motion";
import { SERVICES, IMAGES, T, BRAND } from "../data/content";
import { useLang } from "../context/LangContext";
import { useSiteData } from "../context/SiteDataContext";
import { WhatsAppIcon } from "./icons";

const PriceRow = ({ item, from, i }) => {
  const { tr } = useLang();
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: i * 0.04 }}
      className="group flex items-baseline justify-between gap-4 border-b border-white/10 py-4"
      data-testid="service-row"
    >
      <span className="font-serif text-lg text-[#F5ECDA] transition-colors duration-300 group-hover:text-[#D4AF37] md:text-xl">
        {tr(item)}
      </span>
      <span className="flex-1 mx-3 border-b border-dotted border-white/10 translate-y-[-4px]" />
      <span className="whitespace-nowrap text-sm text-[#B7A78C]">
        <span className="text-[10px] uppercase tracking-widest text-[#B7A78C]/60">{from} </span>
        <span className="font-serif text-lg text-[#D4AF37]">£{item.price}</span>
      </span>
    </motion.div>
  );
};

const Category = ({ title, items, image, reverse }) => {
  const { tr } = useLang();
  const from = tr(T.services.from);
  return (
    <div className={`grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16 ${reverse ? "" : ""}`}>
      <div className={`lg:col-span-4 ${reverse ? "lg:order-2" : ""}`}>
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="relative h-[300px] overflow-hidden rounded-sm lg:sticky lg:top-28 lg:h-[440px]"
        >
          <img src={image} alt={tr(title)} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <h3 className="absolute bottom-6 left-6 right-6 font-serif text-2xl leading-tight text-[#F5ECDA] md:text-3xl">
            {tr(title)}
          </h3>
        </motion.div>
      </div>
      <div className={`lg:col-span-8 ${reverse ? "lg:order-1" : ""}`}>
        <div className="grid grid-cols-1 gap-x-12 md:grid-cols-2">
          {items.map((item, i) => (
            <PriceRow key={item.en} item={item} from={from} i={i} />
          ))}
        </div>
      </div>
    </div>
  );
};

export const Services = () => {
  const { tr } = useLang();
  const { services, brand } = useSiteData();
  return (
    <section id="services" className="relative bg-[#15100B] py-24 lg:py-36" data-testid="services-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-5 flex items-center gap-3">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">{tr(T.services.label)}</span>
            </div>
            <h2 className="font-serif text-5xl tracking-tight text-[#F5ECDA] md:text-6xl">{tr(T.services.title)}</h2>
          </div>
          <p className="max-w-xs text-sm leading-relaxed text-[#B7A78C]">{tr(T.services.note)}</p>
        </div>

        <div className="flex flex-col gap-24 lg:gap-32">
          <Category title={T.services.cat_african} items={services.african} image={IMAGES.african} />
          <Category title={T.services.cat_european} items={services.european} image={IMAGES.european} reverse />

          <div>
            <h3 className="mb-8 font-serif text-2xl italic text-[#D4AF37] md:text-3xl">{tr(T.services.cat_treatments)}</h3>
            <div className="grid grid-cols-1 gap-x-12 md:grid-cols-2 lg:grid-cols-4">
              {services.treatments.map((item, i) => (
                <PriceRow key={item.en + i} item={item} from={tr(T.services.from)} i={i} />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20 flex justify-center">
          <a
            href={`https://wa.me/${brand.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 rounded-full bg-[#D4AF37] px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-black transition-colors duration-300 hover:bg-[#F3E5AB]"
            data-testid="services-book-cta"
          >
            <WhatsAppIcon className="h-4 w-4" />
            {tr(T.nav.book)}
          </a>
        </div>
      </div>
    </section>
  );
};
